import { Box, Typography } from '@mui/material';
import { memo, useMemo } from 'react';

import { TPMS_LEGEND, TPMS_WARNING_CONFIG, TireStatus } from 'utils/tpms';
import { useTranslation } from 'react-i18next';

type MetricType = 'pressure' | 'temperature';

interface TireStatusLegendProps {
  metricType: MetricType;
  fontColor?: string;
  enableDarkTheme?: boolean;
  showBatteryLegend?: boolean;
  showWarningLegend?: boolean;
}

const statusRenderOrder: TireStatus[] = [
  TireStatus.HEALTHY,
  TireStatus.UNHEALTHY,
  TireStatus.WARNING,
  TireStatus.NO_DATA,
  TireStatus.NO_SENSOR,
];

const getStaleBadgeLabel = (label: string) => {
  const match = label.match(/>\s*\d+\s*h?/i);
  if (match) {
    return match[0].replace(/\s+/g, '');
  }

  if (label.toLowerCase().includes('stale')) {
    return '>24h';
  }

  return '';
};

const LegendPill = ({
  primaryColor,
  secondaryColor,
  label,
  secondaryLabel,
  fontColor,
  enableDarkTheme,
}: {
  primaryColor: string;
  secondaryColor?: string;
  label: string;
  secondaryLabel?: string;
  fontColor: string;
  enableDarkTheme: boolean;
}) => {
  const borderColor = enableDarkTheme
    ? 'rgba(255,255,255,0.7)'
    : 'rgba(0,0,0,0.35)';

  const badgeTextColor = '#FFFFFF';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        columnGap: 0.2,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: '6px',
          overflow: 'hidden',
          border: `1px solid ${borderColor}`,
          backgroundColor: secondaryColor ? undefined : primaryColor,
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 20,
            backgroundColor: primaryColor,
          }}
        />
        {secondaryColor ? (
          <Box
            sx={{
              width: 24,
              height: 20,
              backgroundColor: secondaryColor,
              borderLeft: `1px solid ${borderColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '9px',
              fontWeight: 600,
              color: badgeTextColor,
              padding: '0 2px',
            }}
          >
            {secondaryLabel}
          </Box>
        ) : null}
      </Box>
      <Typography
        sx={{
          fontSize: '10px',
          color: fontColor,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export const TireStatusLegend = memo(
  ({
    metricType,
    fontColor = '#FFFFFF',
    enableDarkTheme = true,
    showBatteryLegend = true,
    showWarningLegend,
  }: TireStatusLegendProps) => {
    const { t } = useTranslation();
    const legendGroups = useMemo(() => {
      const filteredLegend = TPMS_LEGEND.filter((item) => {
        const isWarningStatus = item.status === TireStatus.WARNING;
        if (isWarningStatus) {
          return (
            showWarningLegend ??
            TPMS_WARNING_CONFIG[
              metricType.toLowerCase() as keyof typeof TPMS_WARNING_CONFIG
            ] ??
            false
          );
        }

        return true;
      });

      return statusRenderOrder
        .map((status) => {
          const entries = filteredLegend.filter(
            (legendItem) => legendItem.status === status
          );
          if (entries.length === 0) return null;
          return { status, entries };
        })
        .filter(Boolean) as Array<{
        status: TireStatus;
        entries: Array<(typeof TPMS_LEGEND)[number]>;
      }>;
    }, [metricType, showWarningLegend]);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.6,
          flexWrap: 'nowrap',
          padding: enableDarkTheme ? '2px 4px' : '0px',
          overflowX: 'auto',
        }}
      >
        {legendGroups.map(({ entries, status }) => {
          if (entries.length >= 2) {
            const [primary, secondary] = entries;
            return (
              <LegendPill
                key={status}
                primaryColor={primary.color}
                secondaryColor={secondary.color}
                label={t(primary.labelKey)}
                secondaryLabel={getStaleBadgeLabel(t(secondary.labelKey))}
                fontColor={fontColor}
                enableDarkTheme={enableDarkTheme}
              />
            );
          }

          const singleEntry = entries[0];
          return (
            <LegendPill
              key={status}
              primaryColor={singleEntry.color}
              label={t(singleEntry.labelKey)}
              fontColor={fontColor}
              enableDarkTheme={enableDarkTheme}
            />
          );
        })}

        {showBatteryLegend ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              columnGap: 0.25,
              whiteSpace: 'nowrap',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 12 12">
              <path
                d="M6 2 L4 5 L5.5 5 L4.5 8 L7.5 5 L6.5 5 L8 2 Z"
                fill={fontColor}
                stroke={fontColor}
                strokeWidth="0.5"
              />
            </svg>
            <Typography sx={{ fontSize: '10px', color: fontColor }}>
              {t('battery_low')}
            </Typography>
          </Box>
        ) : null}
      </Box>
    );
  }
);

TireStatusLegend.displayName = 'TireStatusLegend';

export default TireStatusLegend;
